import * as yup from 'yup';
import {
  Appointments,
  CustomerFiles,
  CustomerGuests,
  Customers,
  DepositPaymentStatus,
  Employees,
  GeneralRequests,
  GiftCards,
  PaymentMethod,
  PaymentRefundReason,
  PaymentType,
  ServiceCategory,
  Services,
  Users,
  WaiverEventType,
} from '@prisma/client';
import { ACCEPTED_FILE_TYPES } from '../../constants/customers';
import { getFileSchema } from '../global';
import type { CustomerEmergencyContact } from '../../interfaces';

export type CustomerGuest = Pick<
  CustomerGuests,
  'id' | 'firstName' | 'lastName' | 'dob' | 'email' | 'medicalHistory'
> & { note?: string | null };

export type CustomerOverview = Customers & {
  user: Users;
  appointments: (Appointments & {
    service: Services | null;
    employees:
      | (Employees & {
          user: Users;
        })
      | null;
  })[];
  generalRequests: GeneralRequests[];
  internalNotes: string | null;
  files: Pick<CustomerFiles, 'id' | 'url' | 'type' | 'name'>[];
  summary: {
    lastAppointmentVisit: Date | null;
    nextAppointmentVisit: Date | null;
    isActive: boolean;
    totalVisits: number;
    numberOfNoShows: number;
    numberOfCanceledVisits: number;
    noShowRatePct: number | null;
    cancellationRatePct: number | null;
    lifetimeValue: number;
    unusedDepositsTotal: number;
    customerSince: string;
  };
  emergencyContacts: CustomerEmergencyContact[];
  giftCards: Pick<GiftCards, 'id' | 'currentBalance' | 'purchasedAt'>[];
  campaignAttributions?: { id: string; name: string }[];
  customerGuests?: CustomerGuest[];
  isBlocked?: boolean;
  shopBlocked?: boolean;
  artistBlocked?: boolean;
  blockedEmployeeIds?: string[];
  blockedArtists?: { employeeId: string; name: string }[];
};

export type CustomerBlockStatus = {
  isBlocked: boolean;
  shopBlocked: boolean;
  artistBlocked: boolean;
  blockedEmployeeIds: string[];
  blockedArtists: { employeeId: string; name: string }[];
};

export type CustomerStats = CustomerOverview['summary'];

export type ProfileAppointment = CustomerOverview['appointments'][number];

export interface AppointmentsSummaryCounts {
  total: number;
  completed: number;
  noShow: number;
  canceled: number;
}

export type CustomerPaymentTotals = {
  totalPaid: number;
  averageTransaction: number;
  outstandingBalance: number;
};

export const customerFileSchema = yup.object({
  file: getFileSchema(ACCEPTED_FILE_TYPES),
});

export type CustomerFileForm = yup.InferType<typeof customerFileSchema>;

export type TabId =
  | 'profile'
  | 'timeline'
  | 'appointments'
  | 'payments'
  | 'forms-files';

export interface TabDefinition {
  id: TabId;
  label: string;
}

export type TimelineActor = { userId: string; name: string } | null;

interface TimelineEntryBase {
  id: string;
  occurredAt: string;
  actor: TimelineActor;
}

export type AppointmentMilestone =
  | 'submitted'
  | 'accepted'
  | 'scheduled'
  | 'confirmed'
  | 'deposit_paid'
  | 'checked_out'
  | 'canceled'
  | 'no_show';

export interface AppointmentMilestoneEntry extends TimelineEntryBase {
  type: 'appointment_milestone';
  milestone: AppointmentMilestone;
  appointmentId: string;
}

export interface AppointmentEntry extends TimelineEntryBase {
  type: 'appointment';
  appointment: {
    appointmentId: string;
    serviceCategory: ServiceCategory;
    serviceName: string | null;
    employeeName: string | null;
    startDateTime: string | null;
    appointmentLengthInMinutes: number;
    price: number | null;
    internalNotes: string | null;
    isUpcoming: boolean;
    wasNoShow: boolean;
  };
}

export interface PaymentEntry extends TimelineEntryBase {
  type: 'payment';
  payment: {
    paymentId: string;
    type: PaymentType;
    method: PaymentMethod;
    amount: number;
    depositStatus: DepositPaymentStatus | null;
  };
}

export interface PaymentRefundEntry extends TimelineEntryBase {
  type: 'payment_refund';
  refund: {
    refundId: string;
    paymentId: string;
    amount: number;
    reason: PaymentRefundReason | null;
  };
}

export interface WaiverEventEntry extends TimelineEntryBase {
  type: 'waiver_event';
  waiverEvent: {
    waiverRequestId: string;
    eventType: WaiverEventType;
  };
}

export interface CustomerFileEntry extends TimelineEntryBase {
  type: 'customer_file';
  file: {
    fileId: string;
    name: string;
    mimeType: string;
    url: string;
  };
}

export type TimelineEntry =
  | AppointmentMilestoneEntry
  | AppointmentEntry
  | PaymentEntry
  | PaymentRefundEntry
  | WaiverEventEntry
  | CustomerFileEntry;

export interface GetCustomerTimelinePage {
  entries: TimelineEntry[];
  nextCursor: string | null;
}
